import { Suspense, Fragment, lazy } from "react";
import { Route, Routes } from "react-router-dom";
import { useAnalytics } from "./hooks/useGAPageView";

import SplashScreen from "./components/SplashScreen";
import AuthLayout from "./layouts/AuthLayout";
import GuestLayout from "./layouts/GuestLayout";
import UnAuthLayout from "./layouts/UnauthLayout";
import BotPreviewLayout from "./layouts/BotPreviewLayout";
import AuthGuard from "./guards/AuthGuard";
import UnAuthGuard from "./guards/UnAuthGuard";
import AdminGuard from "./guards/AdminGuard";
import AffiliateGuard from "./guards/AffiliateGuard";
import AgencyGuard from "./guards/AgencyGuard";

// import ChatbotModelSettings from "./components/admin/ChatBotModal/ChatbotModelSettings";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Home from "./pages/Home";
import Pricing from "./pages/Pricing";
import Dashboard from "./pages/Dashboard";
import BotSettings from "./pages/BotSettings";
// const BotSettings = lazy(() => import("./pages/BotSettings"));

// import HumanTakeover from "./components/humanTakeover";
const HumanTakeover = lazy(() => import("./components/humanTakeover"));
const ChatbotModelSettings = lazy(
  () => import("./components/admin/ChatBotModal/ChatbotModelSettings"),
);

const Error = lazy(() => import("./pages/404"));

const Documentation = lazy(() => import("./pages/Documentation"));
const OnboardingTutorials = lazy(() => import("./pages/OnboardingTutorials"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const Features = lazy(() => import("./pages/Features"));

const Profile = lazy(() => import("./pages/Profile"));
const SubscriptionPlans = lazy(() => import("./pages/SubscriptionPlans"));
const ChatHistory = lazy(() => import("./pages/ChatHistory"));
const Chatbots = lazy(() => import("./pages/Chatbots"));
const Analytics = lazy(() => import("./pages/Analytics"));

const Settings = lazy(() => import("./pages/Settings"));
// const BotSettings = lazy(() => import("./pages/BotSettings"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const Affiliate = lazy(() => import("./pages/Affiliate"));
const Terms = lazy(() => import("./pages/Terms"));
const EmailVerification = lazy(() => import("./pages/EmailVerification"));
const Migration = lazy(() => import("./pages/Migration"));
const ProductLiterature = lazy(() => import("./pages/ProductLiterature"));
const MarktingUpload = lazy(() => import("./components/admin/MarketingUpload"));
const BotEmbed = lazy(() => import("./components/BotEmbed"));
const ChatBotContact = lazy(
  () => import("./components/chatbot-contacts/ChatBotContact"),
);

const ChatBotViSitorContact = lazy(
  () => import("./components/chatbot-contacts-non-registred/ChatBotContact"),
);

const SiteContentManagement = lazy(
  () => import("./components/admin/SiteContentManagement"),
);
const ContactUsList = lazy(() => import("./components/agent/ContactUsList"));
const PaymentManagement = lazy(() => import("./components/PaymentManagement"));
const RoleManagement = lazy(
  () => import("./components/accessManagement/RoleManagement"),
);

const BotMetaData = lazy(() => import("./components/admin/BotMetaData"));
const PricingQuery = lazy(() => import("./components/admin/PricingQuery"));
const ExchangeRate = lazy(() => import("./components/admin/ExchangeRate"));
const SamplePromptQnA = lazy(
  () => import("./components/admin/SamplePromptQnA"),
);

const EmployeeManagement = lazy(
  () => import("./components/accessManagement/EmployeeManagement"),
);
const GeneratePromocode = lazy(
  () => import("./components/admin/promocode/GeneratePromocode"),
);
const PlanManagement = lazy(() => import("./components/admin/PlanManagement"));
const CreateOrUpdatePlan = lazy(
  () => import("./components/admin/plan/CreateOrUpdatePlan"),
);
const PaymentStatus = lazy(
  () => import("./components/payment-status/PaymentStatus"),
);
const PaymentSuccess = lazy(
  () => import("./components/payment-status/PaymentSuccess"),
);
const PaymentFailure = lazy(
  () => import("./components/payment-status/PaymentFailure"),
);
const AdminLogin = lazy(() => import("./components/admin/AdminLogin"));
const CreateOrUpdateCms = lazy(
  () => import("./components/admin/cms/CreateOrUpdateCms"),
);
const PlanList = lazy(() => import("./components/my-subscription/PlanList"));
const CreateOrUpdateUsers = lazy(
  () => import("./components/admin/users/CreateOrUpdateUsers"),
);
const AffiliateReferalActivity = lazy(
  () => import("./components/Affiliate/AffiliateReferalActivity"),
);
const AffiliateEarning = lazy(
  () => import("./components/Affiliate/AffiliateEarning"),
);
const AccessManagement = lazy(
  () => import("./components/accessManagement/AccessManagement"),
);
const SubAccount = lazy(() => import("./components/subAccount/SubAccount"));
const ReveneuReport = lazy(() => import("./components/admin/ReveneuReport"));
const Notifications = lazy(() => import("./components/Notifications"));
const ConnectGoHighLevelForm = lazy(
  () => import("./components/connectGoHighLevel/ConnectGoHighLevelForm"),
);
const ConnectGoHighLevelCode = lazy(
  () => import("./components/connectGoHighLevel/ConnectGoHighLevelCode"),
);
const FeedbackForm = lazy(() => import("./components/feedback/FeedbackForm"));
const BrandingManagement = lazy(
  () => import("./components/agent/BrandingManagement"),
);
const SubscriptionManagement = lazy(
  () => import("./components/admin/SubscriptionManagement"),
);
const UserManagement = lazy(() => import("./components/admin/UserManagement"));
const PromoCodeManagement = lazy(
  () => import("./components/admin/PromoCodeManagement"),
);
const Cms = lazy(() => import("./components/admin/Cms"));
const EmailConfigurationManagement = lazy(
  () => import("./components/admin/EmailConfigurationManagement"),
);
const SocialMediaLinkManagement = lazy(
  () =>
    import("./components/admin/socialLinkManagment/SocialMediaLinkManagement"),
);
const AgencyManagement = lazy(
  () => import("./components/admin/agencyManagement/AgencyManagement"),
);
const AgencyUserActivity = lazy(
  () => import("./components/admin/agencyManagement/AgencyUserActivity"),
);
const AffiliateManagement = lazy(
  () => import("./components/admin/affiliateManagement/AffiliateManagement"),
);
const AffiliateCommision = lazy(
  () => import("./components/admin/affiliateManagement/AffiliateCommision"),
);
const AffiliateReferralLink = lazy(
  () => import("./components/Affiliate/AffiliateReferralLink"),
);

// export const renderRoutes = (routes = []) => (
//   <AnalyticsWrapper>
//     <Routes>
//       {routes.map((route, i) => {
//         const layout = route.layout || Fragment;

//         return (
//           <Route
//             key={i}
//             path={route.path}
//             element={
//               route.path == "/ghl/connect" ? (
//                 <ConnectGoHighLevelCode />
//               ) : layout == "auth" ? (
//                 <AuthGuard>
//                   <AuthLayout>
//                     <Suspense fallback={<SplashScreen />}>
//                       {route.path == "/dashboard" && <Dashboard />}
//                       {route.path == "/my-chatbots" && <Chatbots />}
//                       {route.path == "/chatbot-registered-contacts" && <ChatBotContact />}
//                       {route.path == "/bot-settings" && <BotSettings />}
//                       {route.path == "/chat-history" && <ChatHistory />}
//                       {route.path == "/my-plan" && <SubscriptionPlans />}
//                       {route.path == "/profile" && <Profile />}
//                       {route.path == "/analytics" && <Analytics />}
//                       {route.path == "/settings" && <Settings />}
//                       {route.path == "/payment-status" && <PaymentStatus />}
//                       {route.path == "/payment-success" && <PaymentSuccess />}
//                       {route.path == "/payment-failure" && <PaymentFailure />}
//                       {route.path == "/plan-list" && <PlanList />}
//                       {route.path == "/help" && <Documentation />}
//                       {route.path == "/role" && <UserManagement />}
//                       {route.path == "/role/create" && <CreateOrUpdateUsers />}
//                       {route.path == "/role/edit/:id" && (
//                         <CreateOrUpdateUsers />
//                       )}
//                       {route.path == "/demo/path1" && <SubAccount />}
//                       {route.path == "/demo/path2" && <CreateOrUpdateUsers />}
//                       {route.path == "/demo/path3" && <CreateOrUpdateUsers />}
//                       {route.path == "/demo/path4" && <CreateOrUpdateUsers />}
//                       {route.path == "/notifications" && <Notifications />}
//                       {route.path == "/ghl/form" && <ConnectGoHighLevelForm />}
//                       {route.path == "/ghl/connect" && (
//                         <ConnectGoHighLevelCode />
//                       )}
//                       {route.path == "/feedback" && <FeedbackForm />}
//                       {route.path == "/role-management" && <RoleManagement />}
//                       {route.path == "/role-management/view" && (
//                         <AccessManagement />
//                       )}
//                       {route.path == "/employee-management" && (
//                         <EmployeeManagement />
//                       )}
//                       {route.path == "/human-takeover" && <HumanTakeover />}
//                       {route.path == "/chatbot-metadata" && <BotMetaData />}
//                     </Suspense>
//                   </AuthLayout>
//                 </AuthGuard>
//               ) : layout == "admin" ? (
//                 <AdminGuard>
//                   <AuthLayout>
//                     <Suspense fallback={<SplashScreen />}>
//                       {route.path == "/admin/dashboard" && <Dashboard />}

//                       {route.path == "/admin/my-chatbots" && <Chatbots />}
//                       {route.path == "/admin/chatbot-registered-contacts" && (
//                         <ChatBotContact />
//                       )}
//                       {route.path == "/admin/bot-settings" && <BotSettings />}
//                       {route.path == "/admin/chat-history" && <ChatHistory />}
//                       {route.path == "/admin/profile" && <Profile />}
//                       {route.path == "/admin/analytics" && <Analytics />}
//                       {route.path == "/admin/settings" && <Settings />}
//                       {route.path == "/admin/customer" && <UserManagement />}
//                       {route.path == "/admin/customer/create/:usertype" && (
//                         <CreateOrUpdateUsers />
//                       )}
//                       {route.path == "/admin/customer/edit/:id" && (
//                         <CreateOrUpdateUsers />
//                       )}
//                       {route.path ==
//                         "/admin/subscription-management/:usertype" && (
//                         <SubscriptionManagement />
//                       )}
//                       {route.path == "/admin/promocode" && (
//                         <PromoCodeManagement />
//                       )}
//                       {route.path == "/admin/promocode/create" && (
//                         <GeneratePromocode />
//                       )}
//                       {route.path == "/admin/plan" && <PlanManagement />}
//                       {route.path == "/admin/plan/create" && (
//                         <CreateOrUpdatePlan />
//                       )}
//                       {route.path == "/admin/plan/edit/:id" && (
//                         <CreateOrUpdatePlan />
//                       )}
//                       {route.path == "/admin/contact-us-management" && (
//                         <ContactUsList />
//                       )}
//                       {route.path == "/admin/cms" && <Cms />}
//                       {route.path == "/admin/cms/create" && (
//                         <CreateOrUpdateCms />
//                       )}
//                       {route.path == "/admin/cms/edit/:id" && (
//                         <CreateOrUpdateCms />
//                       )}
//                       {route.path == "/admin/role-management/view" && (
//                         <AccessManagement />
//                       )}
//                       {route.path == "/admin/revenue-report" && (
//                         <ReveneuReport />
//                       )}
//                       {route.path == "/admin/notifications" && (
//                         <Notifications />
//                       )}
//                       {route.path == "/admin/link-management" && (
//                         <SocialMediaLinkManagement />
//                       )}
//                       {route.path == "/admin/email-sender-configuration" && (
//                         <EmailConfigurationManagement />
//                       )}
//                       {route.path == "/admin/agency-management" && (
//                         <AgencyManagement />
//                       )}
//                       {route.path == "/admin/agency-management/view" && (
//                         <AgencyUserActivity />
//                       )}
//                       {route.path == "/admin/affiliate-management" && (
//                         <AffiliateManagement />
//                       )}
//                       {route.path == "/admin/affiliate-management/view" && (
//                         <AffiliateCommision />
//                       )}
//                       {route.path == "/admin/site-content-management" && (
//                         <SiteContentManagement />
//                       )}
//                       {route.path == "/admin/role-management" && (
//                         <RoleManagement />
//                       )}
//                       {route.path == "/admin/employee-management" && (
//                         <EmployeeManagement />
//                       )}
//                       {route.path == "/admin/human-takeover" && (
//                         <HumanTakeover />
//                       )}
//                       {route.path == "/admin/chatbot-model-settings" && (
//                         <ChatbotModelSettings />
//                       )}
//                       {route.path == "/admin/marketing" && <MarktingUpload />}
//                       {route.path == "/admin/chatbot-metadata" && (
//                         <BotMetaData />
//                       )}
//                       {route.path == "/admin/pricing-query" && <PricingQuery />}
//                       {route.path == "/admin/exchange-rate-setting" && (
//                         <ExchangeRate />
//                       )}
//                       {route.path == "/admin/sample-prompt-and-qna" && (
//                         <SamplePromptQnA />
//                       )}
//                     </Suspense>
//                   </AuthLayout>
//                 </AdminGuard>
//               ) : layout == "affiliate" ? (
//                 <AffiliateGuard>
//                   <AuthLayout>
//                     <Suspense fallback={<SplashScreen />}>
//                       {route.path == "/affiliate/dashboard" && <Dashboard />}
//                       {route.path == "/affiliate/settings" && <Settings />}
//                       {route.path == "/affiliate/referral-activity" && (
//                         <AffiliateReferalActivity />
//                       )}
//                       {route.path == "/affiliate/referral-link" && (
//                         <AffiliateReferralLink />
//                       )}
//                       {route.path == "/affiliate/profile" && <Profile />}
//                       {route.path == "/affiliate/payment-management" && (
//                         <PaymentManagement />
//                       )}
//                       {route.path == "/affiliate/feedback" && <FeedbackForm />}
//                       {route.path == "/affiliate/help" && <Affiliate />}
//                       {route.path == "/affiliate/earning" && (
//                         <AffiliateEarning />
//                       )}
//                       {route.path == "/affiliate/notifications" && (
//                         <Notifications />
//                       )}
//                     </Suspense>
//                   </AuthLayout>
//                 </AffiliateGuard>
//               ) : layout == "agency" ? (
//                 <AgencyGuard>
//                   <AuthLayout>
//                     {route.path == "/agency/dashboard" && <Dashboard />}
//                     {route.path == "/agency/testcomp" && <Dashboard />}
//                     {route.path == "/agency/my-chatbots" && <Chatbots />}
//                     {route.path == "/agency/chatbot-registered-contacts" && (
//                       <ChatBotContact />
//                     )}
//                     {route.path == "/agency/bot-settings" && <BotSettings />}
//                     {route.path == "/agency/chat-history" && <ChatHistory />}
//                     {route.path == "/agency/profile" && <Profile />}
//                     {route.path == "/agency/analytics" && <Analytics />}
//                     {route.path == "/agency/settings" && <Settings />}
//                     {route.path == "/agency/child-client" && <UserManagement />}
//                     {route.path == "/agency/child-client/create" && (
//                       <CreateOrUpdateUsers />
//                     )}
//                     {route.path == "/agency/child-client/edit/:id" && (
//                       <CreateOrUpdateUsers />
//                     )}
//                     {route.path == "/agency/my-plan" && <SubscriptionPlans />}
//                     {route.path == "/agency/payment-status" && (
//                       <PaymentStatus />
//                     )}
//                     {route.path == "/agency/payment-success" && (
//                       <PaymentSuccess />
//                     )}
//                     {route.path == "/agency/payment-failure" && (
//                       <PaymentFailure />
//                     )}
//                     {route.path == "/agency/plan-list" && <PlanList />}
//                     {route.path == "/agency/subscription-management" && (
//                       <SubscriptionManagement />
//                     )}
//                     {route.path == "/agency/promocode" && (
//                       <PromoCodeManagement />
//                     )}
//                     {route.path == "/agency/promocode/create" && (
//                       <GeneratePromocode />
//                     )}
//                     {route.path == "/agency/plan" && <PlanManagement />}
//                     {route.path == "/agency/plan/edit/:id" && (
//                       <CreateOrUpdatePlan />
//                     )}
//                     {route.path == "/agency/branding-management" && (
//                       <BrandingManagement />
//                     )}
//                     {route.path == "/agency/payment-management" && (
//                       <PaymentManagement />
//                     )}
//                     {route.path == "/agency/feedback" && <FeedbackForm />}
//                     {route.path == "/agency/help" && <Documentation />}
//                     {route.path == "/agency/cms" && <Cms />}
//                     {route.path == "/agency/cms/create" && (
//                       <CreateOrUpdateCms />
//                     )}
//                     {route.path == "/agency/cms/edit/:id" && (
//                       <CreateOrUpdateCms />
//                     )}
//                     {route.path == "/agency/revenue-report" && (
//                       <ReveneuReport />
//                     )}
//                     {route.path == "/agency/notifications" && <Notifications />}
//                     {route.path == "/agency/link-management" && (
//                       <SocialMediaLinkManagement />
//                     )}
//                     {route.path == "/agency/email-sender-configuration" && (
//                       <EmailConfigurationManagement />
//                     )}
//                     {route.path == "/agency/site-content-management" && (
//                       <SiteContentManagement />
//                     )}
//                     {route.path == "/agency/contact-us-management" && (
//                       <ContactUsList />
//                     )}
//                     {route.path == "/agency/role-management" && (
//                       <RoleManagement />
//                     )}
//                     {route.path == "/agency/role-management/view" && (
//                       <AccessManagement />
//                     )}
//                     {route.path == "/agency/employee-management" && (
//                       <EmployeeManagement />
//                     )}
//                     {route.path == "/agency/ghl/connect" && (
//                       <ConnectGoHighLevelCode />
//                     )}
//                     {route.path == "/agency/human-takeover" && (
//                       <HumanTakeover />
//                     )}
//                     {route.path == "/agency/chatbot-metadata" && (
//                       <BotMetaData />
//                     )}
//                   </AuthLayout>
//                 </AgencyGuard>
//               ) : layout == "unauth" ? (
//                 route.guard == "unauth" ? (
//                   <UnAuthGuard>
//                     <UnAuthLayout
//                       isHeaderFooter={
//                         route.isHeaderFooter == false ? false : true
//                       }
//                     >
//                       <Suspense fallback={<SplashScreen />}>
//                         {route.path == "/" && <Home />}
//                         {route.path == "/login" && (
//                           <Login type="login" userType="user" />
//                         )}
//                         {route.path == "/admin/login" && (
//                           <AdminLogin type="adminlogin" />
//                         )}
//                         {route.path == "/signup" && (
//                           <Signup type="signup" userType="user" />
//                         )}
//                         {route.path == "/affiliate/signup" && (
//                           <Signup type="signup" userType="user" />
//                         )}
//                         {route.path == "/affiliate/login" && (
//                           <Login type="login" userType="user" />
//                         )}
//                         {route.path == "/forget-password" && (
//                           <Login userType="user" type="forgetPass" />
//                         )}
//                         {route.path == "/admin/forget-password" && (
//                           <Login userType="admin" type="forgetPass" />
//                         )}
//                         {route.path == "/reset-password" && (
//                           <Login userType="user" type="resetPass" />
//                         )}
//                         {route.path == "/admin/reset-password" && (
//                           <Login userType="admin" type="resetPass" />
//                         )}
//                         {route.path == "/email-verification" && (
//                           <EmailVerification
//                             userType="user"
//                             type="emailVerification"
//                           />
//                         )}
//                         {route.path == "/payments-status" && <PaymentStatus />}
//                         {route.path == "/payments-success" && (
//                           <PaymentSuccess />
//                         )}
//                         {route.path == "/payments-failure" && (
//                           <PaymentFailure />
//                         )}
//                       </Suspense>
//                     </UnAuthLayout>
//                   </UnAuthGuard>
//                 ) : (
//                   <GuestLayout>
//                     <Suspense fallback={<SplashScreen />}>
//                       {route.path == "/pricing" && <Pricing />}
//                       {route.path == "/features" && <Features />}
//                       {route.path == "/documentation" && <Documentation />}
//                       {route.path == "/contact-us" && <ContactUs />}
//                       {route.path == "/privacy-policy" && <PrivacyPolicy />}
//                       {route.path == "/refund-policy" && <RefundPolicy />}
//                       {route.path == "/affiliate" && <Affiliate />}
//                       {route.path == "/terms-of-service" && <Terms />}
//                       {route.path == "/migration-consent" && <Migration />}
//                       {route.path == "/product-literature" && (
//                         <ProductLiterature />
//                       )}
//                       {route.path == "/bot-test" && <BotTest />}
//                     </Suspense>
//                   </GuestLayout>
//                 )
//               ) : layout == "bot" ? (
//                 <BotPreviewLayout>
//                   <Suspense fallback={<SplashScreen />}>
//                     {route.path == "/bot-preview" && <BotPreview />}
//                   </Suspense>
//                 </BotPreviewLayout>
//               ) : (
//                 <Suspense fallback={<SplashScreen />}>
//                   <Error />
//                 </Suspense>
//               ) //<LiveChat connectionType={"outside"}/>
//             }
//           />
//         );
//       })}
//     </Routes>
//   </AnalyticsWrapper>
// );

export const renderRoutes = (routes = []) => (
  <Routes>
    {routes.map((route, i) => {
      const Component = route.component || Error;

      const element = (
        <Suspense fallback={<SplashScreen />}>
          <Component {...(route.props || {})} />
        </Suspense>
      );

      switch (route.layout) {
        case "auth":
          return (
            <Route
              key={i}
              path={route.path}
              element={
                <AuthGuard>
                  <AuthLayout>{element}</AuthLayout>
                </AuthGuard>
              }
            />
          );

        case "admin":
          return (
            <Route
              key={i}
              path={route.path}
              element={
                <AdminGuard>
                  <AuthLayout>{element}</AuthLayout>
                </AdminGuard>
              }
            />
          );

        case "agency":
          return (
            <Route
              key={i}
              path={route.path}
              element={
                <AgencyGuard>
                  <AuthLayout>{element}</AuthLayout>
                </AgencyGuard>
              }
            />
          );

        case "affiliate":
          return (
            <Route
              key={i}
              path={route.path}
              element={
                <AffiliateGuard>
                  <AuthLayout>{element}</AuthLayout>
                </AffiliateGuard>
              }
            />
          );

        case "unauth":
          return (
            <Route
              key={i}
              path={route.path}
              element={
                route.guard === "unauth" ? (
                  <UnAuthGuard>
                    <UnAuthLayout
                      isHeaderFooter={route.isHeaderFooter !== false}
                    >
                      {element}
                    </UnAuthLayout>
                  </UnAuthGuard>
                ) : (
                  <GuestLayout>{element}</GuestLayout>
                )
              }
            />
          );

        case "bot":
          return (
            <Route
              key={i}
              path={route.path}
              element={<BotPreviewLayout>{element}</BotPreviewLayout>}
            />
          );

        case "error":
        default:
          return <Route key={i} path={route.path || "*"} element={element} />;
      }
    })}
  </Routes>
);

function AnalyticsWrapper({ children }) {
  // useAnalytics(); // Safe: inside Router
  return <>{children}</>;
}

const routes = [
  //GHL general Route
  // {
  //   exact: true,
  //   path: "/zoho/connect",
  //   component: ConnectGoHighLevelCode,
  //   // layout: "auth",
  // },
  //Unauth Route -- General
  {
    exact: true,
    path: "/",
    layout: "unauth",
    component: Home,
    guard: "unauth",
  },

  {
    exact: true,
    path: "/email-verification",
    layout: "unauth",
    component: EmailVerification,
    guard: "unauth",
    props: { type: "emailVerification", userType: "user" },
  },

  {
    exact: true,
    path: "/login",
    layout: "unauth",
    component: Login,
    guard: "unauth",
    isHeaderFooter: false,
    props: { type: "login", userType: "user" },
  },

  {
    exact: true,
    path: "/reset-password",
    layout: "unauth",
    component: Login,
    guard: "unauth",
    isHeaderFooter: false,
    props: { type: "resetPass", userType: "user" },
  },

  {
    exact: true,
    path: "/signup",
    layout: "unauth",
    component: Signup,
    guard: "unauth",
    isHeaderFooter: false,
    props: { type: "signup", userType: "user" },
  },

  {
    exact: true,
    path: "/forget-password",
    layout: "unauth",
    component: Login,
    guard: "unauth",
    isHeaderFooter: false,
    props: { type: "forgetPass", userType: "user" },
  },

  {
    exact: true,
    path: "/payments-status",
    layout: "unauth",
    component: PaymentStatus,
    guard: "unauth",
    isHeaderFooter: false,
  },

  {
    exact: true,
    path: "/payments-success",
    layout: "unauth",
    component: PaymentSuccess,
    guard: "unauth",
    isHeaderFooter: true,
  },

  {
    exact: true,
    path: "/payments-failure",
    layout: "unauth",
    component: PaymentFailure,
    guard: "unauth",
    isHeaderFooter: true,
  },

  //Unauth Route -- Admin
  {
    exact: true,
    path: "/admin/login",
    layout: "unauth",
    component: AdminLogin,
    guard: "unauth",
    isHeaderFooter: false,
    props: { type: "adminlogin" },
  },

  {
    exact: true,
    path: "/admin/forget-password",
    layout: "unauth",
    component: Login,
    guard: "unauth",
    isHeaderFooter: false,
    props: { userType: "admin", type: "forgetPass" },
  },

  {
    exact: true,
    path: "/admin/reset-password",
    layout: "unauth",
    component: Login,
    guard: "unauth",
    isHeaderFooter: false,
    props: { userType: "admin", type: "resetPass" },
  },

  //Unauth Route -- Affiliate
  {
    exact: true,
    path: "/affiliate/signup",
    layout: "unauth",
    component: Signup,
    guard: "unauth",
    isHeaderFooter: false,
    props: { type: "signup", userType: "user" },
  },

  {
    exact: true,
    path: "/affiliate/login",
    layout: "unauth",
    component: Login,
    isAdmin: true,
    props: { type: "login", userType: "user" },
  },

  //Guest Route
  {
    exact: true,
    path: "/contact-us",
    layout: "unauth",
    component: ContactUs,
  },

  {
    exact: true,
    path: "/features",
    layout: "unauth",
    component: Features,
  },

  {
    exact: true,
    path: "/pricing",
    layout: "unauth",
    component: Pricing,
  },

  {
    exact: true,
    path: "/privacy-policy",
    layout: "unauth",
    component: PrivacyPolicy,
  },

  {
    exact: true,
    path: "/refund-policy",
    layout: "unauth",
    component: RefundPolicy,
  },

  {
    exact: true,
    path: "/affiliate",
    layout: "unauth",
    component: Affiliate,
  },

  {
    exact: true,
    path: "/terms-of-service",
    layout: "unauth",
    component: Terms,
  },

  {
    exact: true,
    path: "/product-literature",
    layout: "unauth",
    component: ProductLiterature,
  },

  {
    exact: true,
    path: "/migration-consent",
    layout: "unauth",
    component: Migration,
  },

  // {
  //   exact: true,
  //   path: "/bot-test",
  //   layout: "unauth",
  //   component: BotEmbed,
  //   props: { mode: "test" },
  // },

  {
    exact: true,
    path: "/documentation",
    layout: "unauth",
    component: Documentation,
  },

  {
    exact: true,
    path: "/documentation/onboarding-tutorials",
    layout: "unauth",
    component: OnboardingTutorials,
    isHeaderFooter: false,
    guard: "unauth",
  },

  // User / Subscriber  Route

  {
    exact: true,
    path: "/dashboard",
    layout: "auth",
    component: Dashboard,
  },

  {
    exact: true,
    path: "/plan-list",
    layout: "auth",
    component: PlanList,
  },

  {
    exact: true,
    path: "/my-chatbots",
    layout: "auth",
    component: Chatbots,
  },
  {
    exact: true,
    path: "/chatbot-registered-contacts",
    layout: "auth",
    component: ChatBotContact,
  },
  {
    exact: true,
    path: "/chatbot-visitor-contacts",
    layout: "auth",
    component: ChatBotViSitorContact,
  },

  {
    exact: true,
    path: "/analytics",
    layout: "auth",
    component: Analytics,
  },

  {
    exact: true,
    path: "/chat-history",
    layout: "auth",
    component: ChatHistory,
  },

  {
    exact: true,
    path: "/my-plan",
    layout: "auth",
    component: SubscriptionPlans,
  },

  {
    exact: true,
    path: "/profile",
    layout: "auth",
    component: Profile,
  },

  {
    exact: true,
    path: "/settings",
    layout: "auth",
    component: Settings,
  },

  {
    exact: true,
    path: "/payment-status",
    layout: "auth",
    component: PaymentStatus,
  },

  {
    exact: true,
    path: "/payment-success",
    layout: "auth",
    component: PaymentSuccess,
  },

  {
    exact: true,
    path: "/payment-failure",
    layout: "auth",
    component: PaymentFailure,
  },

  {
    exact: true,
    path: "/bot-settings",
    layout: "auth",
    component: BotSettings,
  },

  {
    exact: true,
    path: "/help",
    layout: "auth",
    component: Documentation,
  },

  {
    exact: true,
    path: "/help/onboarding-tutorials",
    layout: "auth",
    component: OnboardingTutorials,
  },

  {
    exact: true,
    path: "/role",
    layout: "auth",
    component: UserManagement,
  },

  {
    exact: true,
    path: "/role/create",
    layout: "auth",
    component: CreateOrUpdateUsers,
  },

  {
    exact: true,
    path: "/role/edit/:id",
    layout: "auth",
    component: CreateOrUpdateUsers,
  },
  {
    exact: true,
    path: "/notifications",
    layout: "auth",
    component: Notifications,
  },
  {
    exact: true,
    path: "/ghl/form",
    layout: "auth",
    component: ConnectGoHighLevelForm,
  },
  {
    exact: true,
    path: "/ghl/connect",
    layout: "auth",
    component: ConnectGoHighLevelCode,
  },
  {
    exact: true,
    path: "/feedback",
    layout: "auth",
    component: FeedbackForm,
  },
  {
    exact: true,
    path: "/role-management",
    layout: "auth",
    component: RoleManagement,
  },
  {
    exact: true,
    path: "/role-management/view",
    layout: "auth",
    component: AccessManagement,
  },
  {
    exact: true,
    path: "/employee-management",
    layout: "auth",
    component: EmployeeManagement,
  },
  {
    exact: true,
    path: "/human-takeover",
    layout: "auth",
    component: HumanTakeover,
  },
  {
    exact: true,
    path: "/chatbot-metadata",
    layout: "auth",
    component: BotMetaData,
  },

  //Admin Route

  {
    exact: true,
    path: "/admin/dashboard",
    layout: "admin",
    component: Dashboard,
  },

  {
    exact: true,
    path: "/admin/my-chatbots",
    layout: "admin",
    component: Chatbots,
  },
  {
    exact: true,
    path: "/admin/chatbot-registered-contacts",
    layout: "admin",
    component: ChatBotContact,
  },
  {
    exact: true,
    path: "/admin/chatbot-visitor-contacts",
    layout: "admin",
    component: ChatBotViSitorContact,
  },

  {
    exact: true,
    path: "/admin/bot-settings",
    layout: "admin",
    component: BotSettings,
  },

  {
    exact: true,
    path: "/admin/chat-history",
    layout: "admin",
    component: ChatHistory,
  },

  {
    exact: true,
    path: "/admin/profile",
    layout: "admin",
    component: Profile,
  },

  {
    exact: true,
    path: "/admin/analytics",
    layout: "admin",
    component: Analytics,
  },

  {
    exact: true,
    path: "/admin/settings",
    layout: "admin",
    component: Settings,
  },

  {
    exact: true,
    path: "/admin/customer",
    layout: "admin",
    component: UserManagement,
  },

  {
    exact: true,
    path: "/admin/customer/create/:usertype",
    layout: "admin",
    component: CreateOrUpdateUsers,
  },

  {
    exact: true,
    path: "/admin/customer/edit/:id",
    layout: "admin",
    component: CreateOrUpdateUsers,
  },

  {
    exact: true,
    path: "/admin/subscription-management/:usertype",
    layout: "admin",
    component: SubscriptionManagement,
  },

  {
    exact: true,
    path: "/admin/contact-us-management",
    layout: "admin",
    component: ContactUsList,
  },
  {
    exact: true,
    path: "/admin/plan",
    layout: "admin",
    component: PlanManagement,
  },

  {
    exact: true,
    path: "/admin/plan/create",
    layout: "admin",
    component: CreateOrUpdatePlan,
  },

  {
    exact: true,
    path: "/admin/plan/edit/:id",
    layout: "admin",
    component: CreateOrUpdatePlan,
  },

  {
    exact: true,
    path: "/admin/cms/create",
    layout: "admin",
    component: CreateOrUpdateCms,
  },

  {
    exact: true,
    path: "/admin/cms/edit/:id",
    layout: "admin",
    component: CreateOrUpdateCms,
  },

  {
    exact: true,
    path: "/admin/cms",
    layout: "admin",
    component: Cms,
  },

  {
    exact: true,
    path: "/admin/revenue-report",
    layout: "admin",
    component: ReveneuReport,
  },
  {
    exact: true,
    path: "/admin/notifications",
    layout: "admin",
    component: Notifications,
  },
  {
    exact: true,
    path: "/admin/link-management",
    layout: "admin",
    component: SocialMediaLinkManagement,
  },
  {
    exact: true,
    path: "/admin/email-sender-configuration",
    layout: "admin",
    component: EmailConfigurationManagement,
  },
  {
    exact: true,
    path: "/admin/agency-management",
    layout: "admin",
    component: AgencyManagement,
  },
  {
    exact: true,
    path: "/admin/agency-management/view",
    layout: "admin",
    component: AgencyUserActivity,
  },
  {
    exact: true,
    path: "/admin/affiliate-management",
    layout: "admin",
    component: AffiliateManagement,
  },
  {
    exact: true,
    path: "/admin/affiliate-management/view",
    layout: "admin",
    component: AffiliateCommision,
  },

  {
    exact: true,
    path: "/admin/site-content-management",
    layout: "admin",
    component: SiteContentManagement,
  },
  {
    exact: true,
    path: "/admin/role-management",
    layout: "admin",
    component: RoleManagement,
  },
  {
    exact: true,
    path: "/admin/role-management/view",
    layout: "admin",
    component: AccessManagement,
  },
  {
    exact: true,
    path: "/admin/employee-management",
    layout: "admin",
    component: EmployeeManagement,
  },

  {
    exact: true,
    path: "/admin/human-takeover",
    layout: "admin",
    component: HumanTakeover,
  },
  {
    exact: true,
    path: "/admin/chatbot-model-settings",
    layout: "admin",
    component: ChatbotModelSettings,
  },
  {
    exact: true,
    path: "/admin/marketing",
    layout: "admin",
    component: MarktingUpload,
  },
  {
    exact: true,
    path: "/admin/chatbot-metadata",
    layout: "admin",
    component: BotMetaData,
  },
  {
    exact: true,
    path: "/admin/pricing-query",
    layout: "admin",
    component: PricingQuery,
  },
  {
    exact: true,
    path: "/admin/exchange-rate-setting",
    layout: "admin",
    component: ExchangeRate,
  },
  {
    exact: true,
    path: "/admin/sample-prompt-and-qna",
    layout: "admin",
    component: SamplePromptQnA,
  },

  //Agency Route
  {
    exact: true,
    path: "/agency/dashboard",
    layout: "agency",
    component: Dashboard,
  },

  {
    exact: true,
    path: "/agency/testcomp",
    layout: "agency",
    component: Dashboard,
  },
  {
    exact: true,
    path: "/agency/my-chatbots",
    layout: "agency",
    component: Chatbots,
  },
  {
    exact: true,
    path: "/agency/chatbot-registered-contacts",
    layout: "agency",
    component: ChatBotContact,
  },
  {
    exact: true,
    path: "/agency/chatbot-visitor-contacts",
    layout: "agency",
    component: ChatBotViSitorContact,
  },
  {
    exact: true,
    path: "/agency/bot-settings",
    layout: "agency",
    component: BotSettings,
  },

  {
    exact: true,
    path: "/agency/chat-history",
    layout: "agency",
    component: ChatHistory,
  },

  {
    exact: true,
    path: "/agency/profile",
    layout: "agency",
    component: Profile,
  },

  {
    exact: true,
    path: "/agency/analytics",
    layout: "agency",
    component: Analytics,
  },

  {
    exact: true,
    path: "/agency/settings",
    layout: "agency",
    component: Settings,
  },

  {
    exact: true,
    path: "/agency/child-client",
    layout: "agency",
    component: UserManagement,
  },

  {
    exact: true,
    path: "/agency/child-client/create",
    layout: "agency",
    component: CreateOrUpdateUsers,
  },

  {
    exact: true,
    path: "/agency/child-client/edit/:id",
    layout: "agency",
    component: CreateOrUpdateUsers,
  },

  {
    exact: true,
    path: "/agency/my-plan",
    layout: "agency",
    component: SubscriptionPlans,
  },

  {
    exact: true,
    path: "/agency/payment-status",
    layout: "agency",
    component: PaymentStatus,
  },

  {
    exact: true,
    path: "/agency/payment-success",
    layout: "agency",
    component: PaymentSuccess,
  },

  {
    exact: true,
    path: "/agency/payment-failure",
    layout: "agency",
    component: PaymentFailure,
  },

  {
    exact: true,
    path: "/agency/plan-list",
    layout: "agency",
    component: PlanList,
  },

  {
    exact: true,
    path: "/agency/subscription-management",
    layout: "agency",
    component: SubscriptionManagement,
  },

  {
    exact: true,
    path: "/agency/promocode",
    layout: "agency",
    component: PromoCodeManagement,
  },

  {
    exact: true,
    path: "/agency/promocode/create",
    layout: "agency",
    component: GeneratePromocode,
  },

  {
    exact: true,
    path: "/agency/plan",
    layout: "agency",
    component: PlanManagement,
  },

  {
    exact: true,
    path: "/agency/plan/edit/:id",
    layout: "agency",
    component: CreateOrUpdatePlan,
  },

  {
    exact: true,
    path: "/agency/branding-management",
    layout: "agency",
    component: BrandingManagement,
  },

  {
    exact: true,
    path: "/agency/payment-management",
    layout: "agency",
    component: PaymentManagement,
  },

  {
    exact: true,
    path: "/agency/help",
    layout: "agency",
    component: Documentation,
  },

  {
    exact: true,
    path: "/agency/help/onboarding-tutorials",
    layout: "agency",
    component: OnboardingTutorials,
  },

  {
    exact: true,
    path: "/agency/feedback",
    layout: "agency",
    component: FeedbackForm,
  },

  {
    exact: true,
    path: "/agency/cms/create",
    layout: "agency",
    component: CreateOrUpdateCms,
  },

  {
    exact: true,
    path: "/agency/cms/edit/:id",
    layout: "agency",
    component: CreateOrUpdateCms,
  },

  {
    exact: true,
    path: "/agency/cms",
    layout: "agency",
    component: Cms,
  },

  {
    exact: true,
    path: "/agency/role-management/view",
    layout: "agency",
    component: RoleManagement,
  },

  {
    exact: true,
    path: "/agency/revenue-report",
    layout: "agency",
    component: ReveneuReport,
  },

  {
    exact: true,
    path: "/agency/notifications",
    layout: "agency",
    component: Notifications,
  },

  {
    exact: true,
    path: "/agency/link-management",
    layout: "agency",
    component: SocialMediaLinkManagement,
  },

  {
    exact: true,
    path: "/agency/email-sender-configuration",
    layout: "agency",
    component: EmailConfigurationManagement,
  },

  {
    exact: true,
    path: "/agency/site-content-management",
    layout: "agency",
    component: SiteContentManagement,
  },

  {
    exact: true,
    path: "/agency/contact-us-management",
    layout: "agency",
    component: ContactUsList,
  },
  {
    exact: true,
    path: "/agency/role-management",
    layout: "agency",
    component: RoleManagement,
  },

  {
    exact: true,
    path: "/agency/employee-management",
    layout: "agency",
    component: EmployeeManagement,
  },
  {
    exact: true,
    path: "/agency/ghl/connect",
    layout: "agency",
    component: ConnectGoHighLevelCode,
  },
  {
    exact: true,
    path: "/agency/human-takeover",
    layout: "agency",
    component: HumanTakeover,
  },
  {
    exact: true,
    path: "/agency/chatbot-metadata",
    layout: "agency",
    component: BotMetaData,
  },

  //Affiliate Route

  {
    exact: true,
    path: "/affiliate/dashboard",
    layout: "affiliate",
    component: Dashboard,
  },

  {
    exact: true,
    path: "/affiliate/settings",
    layout: "affiliate",
    component: Settings,
  },

  {
    exact: true,
    path: "/affiliate/referral-activity",
    layout: "affiliate",
    component: AffiliateReferalActivity,
  },

  {
    exact: true,
    path: "/affiliate/payment-management",
    layout: "affiliate",
    component: PaymentManagement,
  },

  {
    exact: true,
    path: "/affiliate/referral-link",
    layout: "affiliate",
    component: AffiliateReferralLink,
  },

  {
    exact: true,
    path: "/affiliate/profile",
    layout: "affiliate",
    component: Profile,
  },

  {
    exact: true,
    path: "/affiliate/earning",
    layout: "affiliate",
    component: AffiliateEarning,
  },

  {
    exact: true,
    path: "/affiliate/help",
    layout: "affiliate",
    component: Affiliate,
  },

  {
    exact: true,
    path: "/affiliate/feedback",
    layout: "affiliate",
    component: FeedbackForm,
  },
  {
    exact: true,
    path: "/affiliate/notifications",
    layout: "affiliate",
    component: Notifications,
  },

  //Demo Route

  {
    exact: true,
    path: "/demo/path1",
    layout: "auth",
  },

  {
    exact: true,
    path: "/demo/path2",
    layout: "auth",
  },

  {
    exact: true,
    path: "/demo/path3",
    layout: "auth",
  },

  {
    exact: true,
    path: "/demo/path4",
    layout: "auth",
  },

  // bot

  {
    exact: true,
    path: "/bot-preview",
    layout: "bot",
    component: BotEmbed,
    props: { mode: "preview" },
  },

  {
    path: "*",
    layout: "error",
    component: Error,
  },
];

export default routes;
